/**
 * SessionExpiredContext.js
 *
 * This file defines a context and a provider component for handling session expiration.
 * It checks the expiration of a JWT token stored in localStorage and displays a popup
 * when the session has expired. The context provides a boolean value indicating whether
 * the session has expired.
 *
 * Example usage:
 * <SessionExpiredProvider>
 *   <YourComponent />
 * </SessionExpiredProvider>
 *
 * @returns {JSX.Element} The SessionExpiredProvider component.
 */

import React, { createContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import SessionExpiredPopup from "../components/SessionExpiredPopup/SessionExpiredPopup";
import axios from "axios";
const backendUrl = process.env.REACT_APP_BACKEND_URL_FOR_FRONTEND;
// Create a context for session expiration
const SessionExpiredContext = createContext();

// Helper function to safely decode tokens
const safelyDecodeToken = (token) => {
  if (!token || typeof token !== "string" || token.split(".").length !== 3) {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Token decode error:", error);
    return null;
  }
};

// Provider component for session expiration context
export const SessionExpiredProvider = ({ children }) => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [remainingTime, setRemainingTime] = useState(null);
  const navigate = useNavigate();

  // Funciton to check if the token is about to expire
  const isTokenExpiringSoon = useCallback(() => {
    const token = localStorage.getItem("token");
    const decodedToken = safelyDecodeToken(token);
    
  // Check BOTH token and decodedToken
  if (!token || !decodedToken) return false;

    const currentTime = Date.now() / 1000;
    // Check if the token will expire in the next 15 minutes
    return decodedToken.exp < currentTime + 900; // 900 seconds = 15 minutes
  }, []);

  // Function to get remaining time in seconds
  const getTokenRemainingTime = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return 0;

    try {
      const decodedToken = safelyDecodeToken(token);
      const currentTime = Date.now() / 1000;
      return Math.max(0, Math.floor(decodedToken.exp - currentTime));
    } catch (error) {
      return 0;
    }
  }, []);

  // Function to refresh the token
  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios({
        url: `${backendUrl}/login/refreshtoken`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        setRemainingTime(getTokenRemainingTime());
        return true; // Explicitly return true on success
      }
      return false; // Explicitly return false if no token in response
    } catch (error) {
      console.error("Error refreshing token:", error);
    }
  }, [getTokenRemainingTime]);

  // Track significant user activity
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    // Only update activity if it's been at least 1 minute since last activity
    // This prevents excessive token refreshes
    if (now - lastActivity > 60000) {
      setLastActivity(now);
      if (isTokenExpiringSoon()) {
        refreshToken();
      }
    }
  }, [isTokenExpiringSoon, refreshToken, lastActivity]);

  // Add countdown timer effect
  useEffect(() => {
    const updateRemainingTime = () => {
      setRemainingTime(getTokenRemainingTime());
    };

    // Initial check
    updateRemainingTime();

    // Update countdown every second
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [getTokenRemainingTime]);

  useEffect(() => {
    // Add event listeners for user activity
    const activityEvents = ["click", "keydown", "submit"];

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      // Remove event listeners on cleanup
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  useEffect(() => {
    // Function to check token expiration
    const checkTokenExpiration = () => {
      const token = localStorage.getItem("token");
      const decodedToken = safelyDecodeToken(token);
      
      if (decodedToken) {
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          setIsSessionExpired(true);
          setShowPopup(true);
          localStorage.removeItem("token");
        }
      }
    };

    // Don't perform token validation on login or 2FA verification pages
    const currentPath = window.location.pathname;
    if (currentPath === "/" || currentPath.includes("verify-totp")) {
      return; // Skip token validation on login/verification paths
    }

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 3600000); // Check every hour
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  // Function to handle closing the popup
  const handleClosePopup = () => {
    setShowPopup(false);
    navigate("/");
  };

  return (
    <SessionExpiredContext.Provider
      value={{
        isSessionExpired,
        remainingTime,
        refreshToken,
        isTokenExpiringSoon,
      }}
    >
      {children}
      {showPopup && (
        <SessionExpiredPopup show={showPopup} onClose={handleClosePopup} />
      )}
    </SessionExpiredContext.Provider>
  );
};

export default SessionExpiredContext;
